import { faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getPlatformPractice } from 'api/platformPractices';
import { Flex, Line, Spinner } from 'components';
import { BackButton } from 'components/BackButton';
import { Button } from 'components/_form';
import { CustomTab } from 'components/Tabs/CustomTab';
import { CustomTabList } from 'components/Tabs/CustomTabList';
import { CustomTabPanel } from 'components/Tabs/CustomTabPanel';
import { CustomTabs } from 'components/Tabs/CustomTabs';
import { H1 } from 'components/Typography/Typography';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { showPlatformApiError } from 'views/PlatformView/platformApiError';
import { IPlatformPractice } from 'types/platformPractice';
import { PriorityLevelBadge } from 'views/AdministrationView/views/TasksView/components/Priorities/components/PriorityLevelBadge/PriorityLevelBadge';
import { Wrapper } from 'views/AdministrationView/views/CompanyAdministrationView/CompanyAdministrationView.styled';
import { EditPlatformPracticeForm } from '../PlatformPracticesView/components/EditPlatformPracticeForm/EditPlatformPracticeForm';
import { PracticeBlocksTab } from './components/PracticeBlocksTab/PracticeBlocksTab';
import { PracticeStagesTab } from './components/PracticeStagesTab/PracticeStagesTab';
import { PracticeThemesTab } from './components/PracticeThemesTab/PracticeThemesTab';

export const PlatformPracticeDetailView = () => {
  const { practiceId } = useParams<{ practiceId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const id = Number(practiceId);

  const [practice, setPractice] = useState<IPlatformPractice>();
  const [isLoading, setIsLoading] = useState(true);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const load = useCallback(async () => {
    if (!id) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const response = await getPlatformPractice(id);
      setPractice(response.data);
    } catch (error) {
      showPlatformApiError(error, t);
    } finally {
      setIsLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Wrapper mb={94} flexDirection="column">
      <Flex width="100%" alignItems="center">
        <BackButton />
        <H1 m={2} variant={['h3', 'h2']} textTransform="uppercase">
          {t('platform.title')} | {practice?.name || '…'}
        </H1>
      </Flex>
      <Line />

      {isLoading ? (
        <Spinner />
      ) : (
        <>
          <Flex alignItems="center" gap="16px" mb={2}>
            {practice && <PriorityLevelBadge priorityLevel={practice} />}
            <Button variant="eucalyptus" onClick={() => setIsEditOpen(true)}>
              <FontAwesomeIcon icon={faPenToSquare} /> {t('platformResources.editPractice')}
            </Button>
          </Flex>

          <CustomTabs selectedTabClassName="is-selected">
            <CustomTabList>
              <Flex flexWrap="wrap">
                <CustomTab>{t('platformResources.tabs.stages')}</CustomTab>
                <CustomTab>{t('platformResources.tabs.themes')}</CustomTab>
                <CustomTab>{t('platformResources.tabs.blocks')}</CustomTab>
              </Flex>
            </CustomTabList>
            <CustomTabPanel>
              <PracticeStagesTab practiceId={id} />
            </CustomTabPanel>
            <CustomTabPanel>
              <PracticeThemesTab practiceId={id} />
            </CustomTabPanel>
            <CustomTabPanel>
              <PracticeBlocksTab practiceId={id} />
            </CustomTabPanel>
          </CustomTabs>
        </>
      )}

      {practice && isEditOpen && (
        <EditPlatformPracticeForm
          isOpen={isEditOpen}
          editingPracticeId={practice.id}
          onCancelClick={() => setIsEditOpen(false)}
          onSubmitSuccess={() => {
            setIsEditOpen(false);
            load();
          }}
          onDeleteSuccess={() => {
            setIsEditOpen(false);
            const methodologyId = practice.methodology?.id;
            navigate(
              methodologyId
                ? `/app/platform/resources/methodologies/${methodologyId}`
                : '/app/platform/resources'
            );
          }}
        />
      )}
    </Wrapper>
  );
};
